import React, { useCallback, useEffect, useRef, useState } from 'react';

import imgWebX from '../../assets/img/events/2079429454234017891-1.jpg';
import imgCyberE71 from '../../assets/img/events/1979431192694047186-1.jpg';
import imgGitex from '../../assets/img/events/1979230180041462023-1.jpg';
import imgEthDelhi from '../../assets/img/events/1969276831187476988-1.jpg';

/*
 * Recent events — our own post cards, built to match the site.
 *
 * WHY NOT platform.twitter.com/widgets.js: the official embed renders in a cross-origin
 * iframe, so X paints its own dim-grey surface inside it and no CSS of ours can reach
 * in. Four grey panels on a pure-black page is what it looked like, and there is no
 * embed parameter that changes it.
 *
 * So these are our cards, carrying the real content. Photo, text, date, like and reply
 * counts were all pulled from X's public syndication endpoint
 * (cdn.syndication.twimg.com/tweet-result?id=...) and are reproduced as-is — nothing is
 * invented. The images are downloaded into src/assets/img/events so they are served
 * from our own domain, hashed and cached by the build.
 *
 * The whole card is one link to the original post on X.
 *
 * Bonus over the real embed: this is plain HTML, so crawlers and AI assistants read the
 * post text and see the images. The X iframe showed them nothing.
 */

const EVENTS = [
  {
    id: '2079429454234017891',
    img: imgWebX,
    alt: 'Safe Edges team at the WebX 2026 summit in Japan',
    event: 'WebX Summit 2026',
    location: 'Tokyo, Japan',
    text: "In our last week at WebX 2026 in Japan 🇯🇵, we're honestly amazed by the energy of Japan's crypto ecosystem.\n\nThe DeFi community here is incredibly mature, innovative, and genuinely focused on building. Every conversation has been insightful, and we've met founders, researchers…",
    date: 'Jul 21, 2026',
    dateTime: '2026-07-21',
    likes: 4,
    replies: 0,
    photoCount: 4,
  },
  {
    id: '1979431192694047186',
    img: imgCyberE71,
    alt: 'Safe Edges on stage at CyberE71 Accelerator Day in Dubai',
    event: 'CyberE71 Accelerator Day',
    location: 'Dubai, UAE',
    text: 'We took over the stage at the #CyberE71 Accelerator Day Dubai to share the idea behind building one of the most advanced solutions in the space #Evmsleuth. Thank you to the CyberE71 team for inviting us to pitch our solution, which the security industry needs so badly.',
    date: 'Oct 18, 2025',
    dateTime: '2025-10-18',
    likes: 7,
    replies: 0,
    photoCount: 1,
  },
  {
    id: '1979230180041462023',
    img: imgGitex,
    alt: 'Safe Edges at the Future Blockchain Summit during GITEX in Dubai',
    event: 'Future Blockchain Summit',
    location: 'Dubai, UAE',
    text: 'Here we are, crashing the Future Blockchain Summit Dubai! We’ve signed multiple long-term partnerships with truly remarkable companies that have been working in Web3 for years, creating real impact in the industry. We are honored to contribute with deep security expertise…',
    date: 'Oct 17, 2025',
    dateTime: '2025-10-17',
    likes: 9,
    replies: 1,
    photoCount: 3,
  },
  {
    id: '1969276831187476988',
    img: imgEthDelhi,
    alt: 'Safe Edges mentoring at the ETH Delhi conference',
    event: 'ETH Delhi',
    location: 'Delhi, India',
    text: 'Hey Fam!\n\nWe’re joining #EthDelhi! We’ll be there as mentors and attending the ETH Delhi conference. Let’s meet to talk about ETH, Security, and your Product, and chill out together!',
    date: 'Sep 20, 2025',
    dateTime: '2025-09-20',
    likes: 6,
    replies: 0,
    photoCount: 1,
  },
];

const XLogo = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

/* Tints hashtags so the copy reads the way it does on X, without X's stylesheet. */
const RichText = ({ text }) =>
  text.split('\n').map((line, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br />}
      {line.split(/(\s+)/).map((tok, ti) =>
        /^#[\w]+$/.test(tok) ? (
          <span key={ti} className="text-teal-300">
            {tok}
          </span>
        ) : (
          <React.Fragment key={ti}>{tok}</React.Fragment>
        )
      )}
    </React.Fragment>
  ));

const Card = ({ item }) => (
  <li className="snap-start shrink-0 w-[82vw] max-w-[340px] sm:w-[340px] list-none">
    <a
      href={`https://x.com/SafeEdges/status/${item.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col h-full rounded-2xl border border-white/10 bg-black overflow-hidden hover:border-teal-400/50 transition-colors duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-black">
        <img
          src={item.img}
          alt={item.alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"
          aria-hidden="true"
        />

        {item.photoCount > 1 && (
          <span className="absolute top-3 right-3 rounded-full bg-black/70 backdrop-blur px-2 py-0.5 text-[11px] text-white/80 tabular-nums">
            1/{item.photoCount}
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-4">
          <h3 className="text-white font-bold text-[15px] leading-tight">{item.event}</h3>
          <p className="text-teal-300/90 text-[11px] mt-0.5">{item.location}</p>
        </div>
      </div>

      <div className="flex flex-col flex-1 p-4">
        <p className="text-gray-400 text-[13px] leading-relaxed flex-1">
          <RichText text={item.text} />
        </p>

        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-white/[0.07] text-[11px] text-gray-600">
          <XLogo className="w-3 h-3 text-gray-600 group-hover:text-gray-400 transition-colors" />
          <span className="text-gray-500">@SafeEdges</span>
          <time dateTime={item.dateTime}>{item.date}</time>
          <span className="ml-auto flex items-center gap-2.5 tabular-nums">
            {item.replies > 0 && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden="true">
                  <path d="M1.75 12A10.25 10.25 0 1 1 12 22.25a10.2 10.2 0 0 1-4.4-.99l-4.6 1.24 1.24-4.6A10.2 10.2 0 0 1 1.75 12z" />
                </svg>
                {item.replies}
              </span>
            )}
            <span className="flex items-center gap-1">
              <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden="true">
                <path d="M12 21.6l-1.45-1.32C5.4 15.6 2 12.5 2 8.75 2 6.02 4.14 4 6.8 4c1.5 0 2.95.7 3.9 1.8L12 7.1l1.3-1.3C14.25 4.7 15.7 4 17.2 4 19.86 4 22 6.02 22 8.75c0 3.75-3.4 6.85-8.55 11.54z" />
              </svg>
              {item.likes}
            </span>
          </span>
        </div>
      </div>
    </a>
  </li>
);

const Arrow = ({ dir, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir === 'prev' ? 'Previous event' : 'Next event'}
    className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] text-white flex items-center justify-center hover:border-teal-400/60 hover:text-teal-300 disabled:opacity-20 disabled:hover:border-white/15 disabled:hover:text-white disabled:cursor-default transition-all duration-300"
  >
    <svg
      viewBox="0 0 24 24"
      className="w-4 h-4"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {dir === 'prev' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
    </svg>
  </button>
);

const RecentEvents = () => {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const sync = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft >= max - 8);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync]);

  const scrollByCards = (delta) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.offsetWidth + 20 : el.clientWidth;
    el.scrollBy({ left: delta * step, behavior: 'smooth' });
  };

  return (
    <section
      className="bg-black py-16 border-t border-gray-800 overflow-hidden"
      aria-labelledby="recent-events-heading"
    >
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-teal-300/80 mb-3">
              In the field
            </p>
            <h2
              id="recent-events-heading"
              className="text-2xl md:text-3xl font-bold text-white leading-snug"
            >
              Recent events &amp; conferences
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <Arrow dir="prev" onClick={() => scrollByCards(-1)} disabled={atStart} />
            <Arrow dir="next" onClick={() => scrollByCards(1)} disabled={atEnd} />
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-8 md:w-20 bg-gradient-to-r from-black to-transparent z-20"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-8 md:w-20 bg-gradient-to-l from-black to-transparent z-20"
          aria-hidden="true"
        />

        <ul
          ref={trackRef}
          className="flex gap-5 items-stretch overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 py-1 m-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollPaddingLeft: '1.5rem' }}
          role="region"
          aria-roledescription="carousel"
          aria-label="Recent events attended by Safe Edges"
          tabIndex={0}
        >
          {/* Aligns the first card with the container gutter on wide screens */}
          <li
            className="shrink-0 w-0 xl:w-[max(0px,calc((100vw-72rem)/2-1.5rem))] list-none"
            aria-hidden="true"
          />
          {EVENTS.map((item) => (
            <Card key={item.id} item={item} />
          ))}
          <li className="shrink-0 w-6 list-none" aria-hidden="true" />
        </ul>
      </div>
    </section>
  );
};

export { EVENTS };
export default RecentEvents;
